/**
 * Hilfe & Support — Design 11d: Panel von rechts, aus dem Konto-Menü von überall erreichbar.
 * Suche, beliebte Themen, direkter Kontakt, „Was ist neu" und Systemstatus.
 * Statischer Hilfe-Inhalt mit echten Zielen (Wissens-DB, mailto/tel). Steuerung: open + onClose.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "./components";

// TODO: echte Support-Kontaktdaten des Betreibers eintragen (aktuell Platzhalter).
const SUPPORT_EMAIL = "support@maestro-app.de";
const SUPPORT_PHONE = "+49 89 24218200";
const APP_VERSION = "v2.4.1";

const POPULAR: { label: string; q: string }[] = [
  { label: "Angebot mit KI erstellen", q: "Angebot KI erstellen" },
  { label: "Speisekarte importieren", q: "Speisekarte importieren" },
  { label: "Zahlungen mit Stripe einrichten", q: "Stripe Zahlungen einrichten" },
  { label: "Öffentlichen KI-Chat aktivieren", q: "öffentlicher KI-Chat aktivieren" },
];

export function HelpPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  function goWissen(q?: string) {
    onClose();
    navigate(q ? `/wissen?q=${encodeURIComponent(q)}` : "/wissen");
  }

  return (
    <div className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/30 transition-opacity duration-200 dark:bg-black/50 ${open ? "opacity-100" : "opacity-0"}`}
      />
      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Hilfe & Support"
        className={`absolute right-0 top-0 flex h-full w-full max-w-[400px] flex-col bg-surface-container-lowest shadow-2xl transition-transform duration-200 dark:bg-night-card ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-outline-variant/30 px-5 py-4 dark:border-white/10">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary">
            <Icon name="help" className="text-[20px]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-headline-sm text-[16px] font-bold text-on-background dark:text-inverse-on-surface">
              Hilfe &amp; Support
            </h2>
            <p className="font-body-sm text-[12px] text-on-surface-variant dark:text-surface-variant">
              Antworten, Anleitungen und der direkte Draht zu uns.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Schließen"
            className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-background dark:text-surface-variant dark:hover:bg-white/5"
          >
            <Icon name="close" className="text-[20px]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Suche */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              goWissen(query.trim() || undefined);
            }}
            className="relative"
          >
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant dark:text-surface-variant"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hilfe durchsuchen …"
              className="w-full rounded-[9px] border border-outline-variant/40 bg-surface-container-high py-2.5 pl-9 pr-3 text-[13px] text-on-background outline-none transition placeholder:text-on-surface-variant/70 focus:border-primary/40 dark:border-white/10 dark:bg-white/5 dark:text-inverse-on-surface"
            />
          </form>

          {/* Beliebte Themen */}
          <div className="mt-6">
            <h3 className="mb-2 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant dark:text-surface-variant">
              Beliebte Themen
            </h3>
            <div className="overflow-hidden rounded-xl border border-outline-variant/30 dark:border-white/10">
              {POPULAR.map((t, i) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => goWissen(t.q)}
                  className={`flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-surface-container-high dark:hover:bg-white/5 ${
                    i > 0 ? "border-t border-outline-variant/20 dark:border-white/[0.06]" : ""
                  }`}
                >
                  <Icon name="article" className="text-[18px] text-on-surface-variant dark:text-surface-variant" />
                  <span className="flex-1 font-body-sm text-[13px] font-medium text-on-background dark:text-inverse-on-surface">
                    {t.label}
                  </span>
                  <Icon name="chevron_right" className="text-[18px] text-on-surface-variant/60 dark:text-surface-variant/60" />
                </button>
              ))}
            </div>
          </div>

          {/* Persönlicher Kontakt */}
          <div className="mt-6 rounded-xl border border-outline-variant/30 p-4 dark:border-white/10">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {["LM", "TK"].map((in_) => (
                  <div
                    key={in_}
                    className="grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-[11px] font-bold text-primary ring-2 ring-surface-container-lowest dark:ring-night-card"
                  >
                    {in_}
                  </div>
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-body-sm text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                  Support-Team schreiben
                </p>
                <p className="font-body-sm text-[11.5px] text-on-surface-variant dark:text-surface-variant">
                  Antwort meist in unter 2 Stunden
                </p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <a href={`mailto:${SUPPORT_EMAIL}`} className="btn-primary flex-1 justify-center">
                <Icon name="mail" className="text-[18px]" /> Nachricht schreiben
              </a>
              <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`} className="btn-secondary justify-center">
                <Icon name="call" className="text-[18px]" /> Anrufen
              </a>
            </div>
          </div>

          {/* Was ist neu + Systemstatus */}
          <div className="mt-6 space-y-2">
            <button
              type="button"
              onClick={() => goWissen("Was ist neu")}
              className="flex w-full items-center gap-3 rounded-xl border border-outline-variant/30 px-3.5 py-3 text-left transition-colors hover:bg-surface-container-high dark:border-white/10 dark:hover:bg-white/5"
            >
              <Icon name="auto_awesome" className="text-[18px] text-primary" />
              <span className="flex-1 font-body-sm text-[13px] font-medium text-on-background dark:text-inverse-on-surface">
                Was ist neu
              </span>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10.5px] font-semibold text-primary">2 neu</span>
            </button>
            <div className="flex items-center gap-3 rounded-xl border border-outline-variant/30 px-3.5 py-3 dark:border-white/10">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500/60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
              </span>
              <span className="flex-1 font-body-sm text-[13px] font-medium text-on-background dark:text-inverse-on-surface">
                Systemstatus
              </span>
              <span className="font-body-sm text-[11.5px] text-on-surface-variant dark:text-surface-variant">
                Alle Systeme betriebsbereit
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-outline-variant/30 px-5 py-3 dark:border-white/10">
          <span className="flex items-center gap-1.5 font-body-sm text-[11.5px] text-on-surface-variant dark:text-surface-variant">
            Tastenkürzel
            <kbd className="rounded-md border border-outline-variant/60 bg-surface-container-high px-1.5 py-0.5 font-sans text-[10.5px] font-semibold dark:border-white/10 dark:bg-white/5">
              ⌘ /
            </kbd>
          </span>
          <span className="font-body-sm text-[11px] text-on-surface-variant/70 dark:text-surface-variant/70">
            MAESTRO {APP_VERSION}
          </span>
        </div>
      </aside>
    </div>
  );
}
